import type { Column, Schema } from '@hyphen-db-studio/schema-core';

function needsQuotes(identifier: string): boolean {
  return !/^[A-Za-z_][\w]*$/.test(identifier);
}

function ident(name: string): string {
  return needsQuotes(name) ? `"${name}"` : name;
}

function columnSettingsToString(column: Column): string {
  const parts: string[] = [];
  if (column.settings.pk) parts.push('pk');
  if (column.settings.unique) parts.push('unique');
  if (column.settings.notNull) parts.push('not null');
  if (column.settings.increment) parts.push('increment');
  if (column.settings.default !== undefined && column.settings.default !== '') parts.push(`default: ${column.settings.default}`);
  if (column.settings.note) parts.push(`note: '${column.settings.note.replace(/'/g, "\\'")}'`);
  return parts.length ? ` [${parts.join(', ')}]` : '';
}

function columnToLine(column: Column): string {
  return `  ${ident(column.name)} ${column.type}${columnSettingsToString(column)}`;
}

export function generateDbml(schema: Schema): string {
  const lines: string[] = [];

  for (const en of schema.enums) {
    lines.push(`Enum ${ident(en.name)} {`);
    for (const value of en.values) {
      lines.push(`  ${ident(value.name)}${value.note ? ` [note: '${value.note}']` : ''}`);
    }
    lines.push('}', '');
  }

  for (const table of schema.tables) {
    lines.push(`Table ${ident(table.name)} {`);
    for (const column of table.columns) {
      lines.push(columnToLine(column));
    }
    if (table.note) {
      lines.push('', `  Note: '${table.note.replace(/'/g, "\\'")}'`);
    }
    lines.push('}', '');
  }

  for (const rel of schema.relationships) {
    const fromTable = schema.tables.find((t) => t.id === rel.fromTableId);
    const fromColumn = fromTable?.columns.find((c) => c.id === rel.fromColumnId);
    const toTable = schema.tables.find((t) => t.id === rel.toTableId);
    const toColumn = toTable?.columns.find((c) => c.id === rel.toColumnId);
    if (!fromTable || !fromColumn || !toTable || !toColumn) continue;
    lines.push(
      `Ref: ${ident(fromTable.name)}.${ident(fromColumn.name)} ${rel.type} ${ident(toTable.name)}.${ident(toColumn.name)}`,
    );
  }
  if (schema.relationships.length) lines.push('');

  for (const group of schema.groups) {
    lines.push(`TableGroup ${ident(group.name)} {`);
    if (group.color) lines.push(`  color: ${group.color}`);
    for (const tableId of group.tableIds) {
      const table = schema.tables.find((t) => t.id === tableId);
      if (table) lines.push(`  ${ident(table.name)}`);
    }
    lines.push('}', '');
  }

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd() + '\n';
}
